import { Config } from '../Config';

export interface I18nMap {
    [key: string]: string;
}

const notFound = Symbol('ir-translation-not-found');

export class LocalizationUtility {
    constructor(private readonly config: Config, private readonly labelMap: I18nMap) {
        this.translate = this.translate.bind(this);
    }

    public translate(key: string, args: any[] = []): string {
        if (key === '') {
            return '';
        }
        const matchingKey = this.findMatchingKey(key);
        if (matchingKey === notFound || typeof matchingKey !== 'string') {
            return 'Translation for `'+this.replacePlaceholders(key, args) + '` not found (' + this.config.twoLetterIsoCode + ')';
        }
        const translation = this.labelMap[matchingKey];

        return this.replacePlaceholders(translation || key, args);
    }

    public hasTranslation(key: string): boolean {
        return notFound !== this.findMatchingKey(key);
    }

    public get localeCode(): string {
        return this.config.twoLetterIsoCode;
    }

    private findMatchingKey(key: string): string | Symbol {
        const labelMap = this.labelMap;
        if (typeof labelMap[key] !== 'undefined') {
            return key;
        }

        const keyLowercase = key.toLowerCase();
        if (keyLowercase !== key && typeof labelMap[keyLowercase] !== 'undefined') {
            return keyLowercase;
        }

        const keyNoUmlauts = this.replaceUmlauts(key);
        const hasUmlauts = keyNoUmlauts !== key;
        if (hasUmlauts) {
            if (typeof labelMap[keyNoUmlauts] !== 'undefined') {
                return keyNoUmlauts;
            } else {
                return this.findMatchingKey(keyNoUmlauts);
            }
        }

        if (/ /.test(key)) {
            return this.findMatchingKey(key.replace(/ /g, '_'));
        }

        return notFound;
    }

    private replacePlaceholders(translation: string, args: any[]): string {
        let output = translation;
        const regex = /%(\d*\.\d+)?([sfd])/;
        for (const arg of args) {
            const result = regex.exec(output);
            if (result === null) {
                continue;
            }
            const prepared = this.prepareArg(result[2], result[1], arg);
            output = output.substr(0, result.index) + prepared + output.substr(result.index + result[0].length);
        }

        return output;
    }

    private prepareArg(formatter: string, options: string | undefined, arg: any): string {
        switch (formatter) {
            case 'd':
                return parseInt(arg, 10).toFixed(0);
            case 'f':
                return this.formatFloat(options, parseFloat(arg));
            case 's':
            default :
                return '' + arg;
        }
    }

    private formatFloat(options: string | undefined, arg: number) {
        const defaultDigits = 6;
        if (!options) {
            return arg.toFixed(defaultDigits);
        }
        const fractionDigits = options[0] === '.'
            ? parseInt(options.substr(1), 10)
            : (options.substr(0, 2) === '0.' ? parseInt(options.substr(2), 10) : defaultDigits);

        return arg.toFixed(isNaN(fractionDigits) ? defaultDigits : fractionDigits);
    }

    private replaceUmlauts(input: string): string {
        // Ä, ä     \u00c4, \u00e4
        // Ü, ü     \u00dc, \u00fc
        // Ö, ö     \u00d6, \u00f6
        // ß        \u00df
        return input
            .replace(/\u00c4/g, 'Ae')
            .replace(/\u00e4/g, 'ae')
            .replace(/\u00dc/g, 'Ue')
            .replace(/\u00fc/g, 'ue')
            .replace(/\u00d6/g, 'Oe')
            .replace(/\u00f6/g, 'oe')
            .replace(/\u00df/g, 'ss');
    }
}
